import { useState, useEffect } from "react";
import { AdminField, AdminSaveButton, AdminSection } from "@/admin/AdminComponents";
import ImageUpload from "@/admin/ImageUpload";
import { Plus, Trash2, GripVertical } from "lucide-react";

const ProjectsEditor = ({ data, onSave, saving }) => {
  const [form, setForm] = useState({});

  useEffect(() => { setForm({ ...data }); }, [data]);

  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));
  const updateItem = (idx, key, val) => {
    const items = [...(form.items || [])];
    items[idx] = { ...items[idx], [key]: val };
    setForm((p) => ({ ...p, items }));
  };
  const removeItem = (idx) => {
    const items = [...(form.items || [])];
    items.splice(idx, 1);
    setForm((p) => ({ ...p, items }));
  };
  const addItem = () => {
    const items = [...(form.items || [])];
    items.push({
      id: `project-${Date.now()}`,
      title: "",
      description: "",
      cta: "Learn More",
      href: "#",
      image: "",
      comingSoon: false,
    });
    setForm((p) => ({ ...p, items }));
  };
  const moveItem = (idx, dir) => {
    const items = [...(form.items || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    setForm((p) => ({ ...p, items }));
  };

  return (
    <div data-testid="projects-editor">
      <AdminSection title="Section Header">
        <AdminField label="Section Label" value={form.label} onChange={(v) => update("label", v)} />
        <AdminField label="Heading" value={form.heading} onChange={(v) => update("heading", v)} />
      </AdminSection>

      <AdminSection title="Project Cards" description="Manage project cards. Toggle 'Coming Soon' for unreleased projects.">
        {(form.items || []).map((item, idx) => (
          <div key={item.id || idx} className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => moveItem(idx, -1)} className="p-0.5 text-neutral-600 hover:text-white transition-colors" title="Move up">
                    <GripVertical className="w-3.5 h-3.5" />
                  </button>
                </div>
                <span className="text-xs text-neutral-500 font-['Manrope'] font-medium">
                  {item.title || `Project ${idx + 1}`}
                </span>
                {item.comingSoon && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-[10px] text-amber-400 font-medium uppercase tracking-wider">
                    Coming Soon
                  </span>
                )}
              </div>
              <button
                data-testid={`remove-project-${idx}`}
                onClick={() => removeItem(idx)}
                className="p-1.5 rounded-md text-neutral-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <AdminField label="Title" value={item.title} onChange={(v) => updateItem(idx, "title", v)} />
              <AdminField label="CTA Button Text" value={item.cta} onChange={(v) => updateItem(idx, "cta", v)} />
            </div>
            <AdminField label="Description" value={item.description} onChange={(v) => updateItem(idx, "description", v)} rows={2} />
            <AdminField label="Link URL" value={item.href} onChange={(v) => updateItem(idx, "href", v)} placeholder="https://..." />
            <ImageUpload label="Card Image" value={item.image} onChange={(v) => updateItem(idx, "image", v)} />

            <div className="flex items-center gap-3 pt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={item.comingSoon || false}
                  onChange={(e) => updateItem(idx, "comingSoon", e.target.checked)}
                  className="w-4 h-4 rounded bg-white/[0.04] border-white/[0.15] text-white focus:ring-white/50 accent-white"
                />
                <span className="text-xs text-neutral-400 font-['Manrope']">Coming Soon</span>
              </label>
            </div>
          </div>
        ))}

        <button
          data-testid="add-project-button"
          onClick={addItem}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-white/[0.1] text-sm text-neutral-500 font-['Manrope'] hover:border-white/[0.2] hover:text-white transition-colors duration-200"
        >
          <Plus className="w-4 h-4" /> Add Project Card
        </button>
      </AdminSection>

      <AdminSaveButton onClick={() => onSave(form)} saving={saving} />
    </div>
  );
};

export default ProjectsEditor;
